import {
  Context,
  Duration,
  Effect,
  Fiber,
  Layer,
  Queue,
  Ref,
  Schedule,
  type Scope,
} from 'effect';
import {
  type FollowCommand,
  type FollowConfig,
  type FollowEvent,
  type FollowState,
  type FollowViewport,
  followReduce,
  initialFollowState,
  type ScrollTarget,
} from './follow-machine';

/**
 * Effect-first runtime for the pure follow machine. The machine itself stays
 * a plain reducer (it is the behavior spec, exercised directly by property
 * tests); this service is everything around it: a sampler fiber on its own
 * cadence (~5 Hz — decisions don't need the 20fps highlight render pump), an
 * event queue that serializes reduction, command execution through the
 * platform adapter service, and settle-detection for animated scrolls as a
 * supervised polling fiber. Everything is owned by the layer's Scope, so
 * interrupting the program tears the whole thing down — there are no manual
 * timers anywhere.
 *
 * Founding constraint, and the reason every wait below carries a poll bound:
 * NEVER wait unboundedly on state you don't own. The sampler is the most
 * load-bearing fiber in the engine — if it blocks, follow silently stops with
 * no error to observe, because `swallowDefect` catches defects, not a fiber
 * that never resumes.
 *
 * Design: docs/projects/active/read-along-follow-unification.md
 */

// ---------------------------------------------------------------------------
// Platform adapter service

/** Anchors are OPAQUE to the engine: it never inspects them, only passes
 * them between the adapter's methods and dedupes with the adapter's own
 * equality. Each surface picks its honest identity — a doc-pack word anchor,
 * an active highlight DOM element, a `{blockIndex, wordIndex}` pair, … */
// oxlint-disable-next-line typescript/no-generated-empty-object-type -- any non-nullish value is a valid opaque anchor; `undefined` stays distinct as "no anchor"
export type FollowAnchor = NonNullable<unknown>;

/** The sync surface a platform view implements (DOM reads / scrollTo on web,
 * FlashList/ScrollView refs on RN). Bridged into the adapter service with
 * `syncFollowAdapter`; tests provide scripted implementations directly. */
export type SyncReadAlongFollowAdapter<A extends FollowAnchor = FollowAnchor> =
  {
    /** Current playback anchor (fresh object per call is fine — the
     * controller dedupes with `anchorsEqual`). */
    readonly getAnchor: () => A | undefined;
    /** Content equality for anchors — what makes two samples "the same
     * word". Reference equality is fine when the anchor IS an identity
     * (e.g. a DOM element). */
    readonly anchorsEqual: (a: A, b: A) => boolean;
    /** Rest-placement scroll target for the anchor given the view's current
     * layout. Return `{kind:'page'}` when the word's row isn't measured yet,
     * null when the anchor can't be resolved at all. May be async on
     * platforms that measure native views (RN). */
    readonly resolveTarget: (
      anchor: A,
    ) => ScrollTarget | null | Promise<ScrollTarget | null>;
    readonly getViewport: () => FollowViewport;
    /** Whether the active word is visible in the viewport. `target` is the
     * resolution for the same tick, so adapters can answer without extra
     * layout reads. */
    readonly isActiveVisible: (
      anchor: A | undefined,
      target: ScrollTarget | null,
    ) => boolean;
    /** Start a scroll. Animated offset scrolls settle via the controller's
     * viewport polling; a returned promise (coarse RN page jumps) is awaited
     * before the scroll is considered executed. */
    readonly executeScroll: (
      target: ScrollTarget,
      options: { readonly animated: boolean },
    ) => void | Promise<void>;
    /** Stop an in-flight programmatic scroll, if the platform needs to. */
    readonly cancelScroll?: () => void;
    /** Optional imperative pill hook; useReadAlongFollow already mirrors pill
     * visibility into React state. */
    readonly onPillChange?: (visible: boolean) => void;
  };

export type ReadAlongFollowAdapterShape = {
  readonly getAnchor: Effect.Effect<FollowAnchor | undefined>;
  readonly anchorsEqual: (a: FollowAnchor, b: FollowAnchor) => boolean;
  readonly resolveTarget: (
    anchor: FollowAnchor,
  ) => Effect.Effect<ScrollTarget | null>;
  readonly getViewport: Effect.Effect<FollowViewport>;
  readonly isActiveVisible: (
    anchor: FollowAnchor | undefined,
    target: ScrollTarget | null,
  ) => Effect.Effect<boolean>;
  readonly executeScroll: (
    target: ScrollTarget,
    options: { readonly animated: boolean },
  ) => Effect.Effect<void>;
  readonly cancelScroll: Effect.Effect<void>;
  readonly onPillChange: (visible: boolean) => Effect.Effect<void>;
};

export class ReadAlongFollowAdapter extends Context.Service<
  ReadAlongFollowAdapter,
  ReadAlongFollowAdapterShape
>()('ReadAlongFollowAdapter') {}

/** Only route through a Promise when the adapter actually returned one: a
 * Promise suspends the fiber on a real microtask, so work that a sync adapter
 * completes within one `TestClock.adjust` step would instead land after it.
 * Sync adapters (every web surface) must stay fully synchronous. */
const maybeAsync = <T>(value: T | Promise<T>): Effect.Effect<T> =>
  value instanceof Promise
    ? Effect.promise(() => value)
    : Effect.succeed(value);

/** Bridge a sync platform adapter into the adapter service. The generic
 * anchor type is erased here — the engine treats anchors as opaque tokens
 * that only ever flow back into the same adapter. */
export const syncFollowAdapter = <A extends FollowAnchor>(
  sync: SyncReadAlongFollowAdapter<A>,
): ReadAlongFollowAdapterShape => ({
  getAnchor: Effect.sync(() => sync.getAnchor()),
  anchorsEqual: (a, b) => sync.anchorsEqual(a as A, b as A),
  resolveTarget: (anchor) =>
    Effect.suspend(() => maybeAsync(sync.resolveTarget(anchor as A))),
  getViewport: Effect.sync(() => sync.getViewport()),
  isActiveVisible: (anchor, target) =>
    Effect.sync(() => sync.isActiveVisible(anchor as A | undefined, target)),
  executeScroll: (target, options) =>
    Effect.suspend(() => maybeAsync(sync.executeScroll(target, options))),
  cancelScroll: Effect.sync(() => sync.cancelScroll?.()),
  onPillChange: (visible) => Effect.sync(() => sync.onPillChange?.(visible)),
});

export const syncFollowAdapterLayer = <A extends FollowAnchor>(
  sync: SyncReadAlongFollowAdapter<A>,
): Layer.Layer<ReadAlongFollowAdapter> =>
  Layer.succeed(ReadAlongFollowAdapter, syncFollowAdapter(sync));

// ---------------------------------------------------------------------------
// Controller service

export type ReadAlongFollowHandle = {
  /** Wire to USER gestures only (wheel/touch/keys on web, onScrollBeginDrag
   * on RN) — programmatic scrolls must never disengage. */
  readonly notifyUserScrollStart: Effect.Effect<void>;
  readonly notifyUserScrollEnd: Effect.Effect<void>;
  /** Explicit "stop following" without a gesture — e.g. an outline/chapter
   * navigation that scrolls the view away from playback. */
  readonly disengage: Effect.Effect<void>;
  /** Pill tap: re-engage and snap to the current anchor now. */
  readonly engage: Effect.Effect<void>;
  /** Word tap: re-engage without an immediate scroll — the tap's seek moves
   * the anchor and THAT scrolls (avoids jumping to the old word first). */
  readonly engageQuiet: Effect.Effect<void>;
  readonly setPref: (enabled: boolean) => Effect.Effect<void>;
  /** Resize, font change, zoom, virtualizer remount. */
  readonly layoutInvalidated: Effect.Effect<void>;
  /** Test/debug introspection only — platform code must not branch on it. */
  readonly currentState: Effect.Effect<FollowState>;
};

export class ReadAlongFollow extends Context.Service<
  ReadAlongFollow,
  ReadAlongFollowHandle
>()('ReadAlongFollow') {}

export type ReadAlongFollowOptions = {
  readonly config: FollowConfig;
  readonly prefEnabled: boolean;
  /** Anchor sampling cadence. ~5 Hz is plenty for scroll decisions. */
  readonly sampleIntervalMs?: number;
};

const DEFAULT_SAMPLE_INTERVAL_MS = 200;
const SETTLE_POLL_MS = 80;
const SETTLE_TOLERANCE_PX = 2;
const ENGAGE_STABLE_POLL_MS = 32;
/** Engage waits for the viewport to stop moving before it samples, but gives
 * up after ~600ms — a viewport still moving that long is being driven by
 * something else, and engage is explicit user intent that must not stall. */
const ENGAGE_STABLE_MAX_POLLS = 19;
/** Give up polling after ~1.5s — a smooth scroll that long has been
 * interrupted by something; treat it as settled rather than wedged. */
const SETTLE_MAX_POLLS = 19;
/** Bound for the sampler's idle gate. The machine can legitimately sit in
 * `animating` for as long as settle detection runs, so this is derived from
 * that ceiling (2× headroom) rather than picked — a healthy slow scroll must
 * never trip it. Exhausting it means the machine-side invariant guarded by
 * read-along-follow-machine.test.ts ("animating always has a scroll in
 * flight") broke, so it logs and samples anyway. See the module header: the
 * sampler must never block forever on state another file owns. */
const MACHINE_IDLE_MAX_POLLS = Math.ceil(
  (2 * SETTLE_MAX_POLLS * SETTLE_POLL_MS) / ENGAGE_STABLE_POLL_MS,
);

export const makeReadAlongFollow = ({
  config,
  prefEnabled,
  sampleIntervalMs = DEFAULT_SAMPLE_INTERVAL_MS,
}: ReadAlongFollowOptions): Effect.Effect<
  ReadAlongFollowHandle,
  never,
  ReadAlongFollowAdapter | Scope.Scope
> =>
  Effect.gen(function* () {
    const adapter = yield* ReadAlongFollowAdapter;
    const scope = yield* Effect.scope;
    const stateRef = yield* Ref.make(initialFollowState(prefEnabled));
    const events = yield* Queue.unbounded<FollowEvent>();
    const lastAnchorRef = yield* Ref.make<FollowAnchor | undefined>(undefined);
    const sameAnchor = (
      a: FollowAnchor | undefined,
      b: FollowAnchor | undefined,
    ) =>
      a === undefined || b === undefined ? a === b : adapter.anchorsEqual(a, b);
    /** Force a re-resolve on the next tick even if the anchor didn't change
     * (layout changed, pref flipped on, coarse page jump landed, …). */
    const dirtyRef = yield* Ref.make(true);
    const settleFiberRef = yield* Ref.make<Fiber.Fiber<void> | null>(null);

    const anchorEvent = Effect.gen(function* () {
      const anchor = yield* adapter.getAnchor;
      const target = anchor ? yield* adapter.resolveTarget(anchor) : null;
      const event = {
        type: 'anchor',
        target,
        viewport: yield* adapter.getViewport,
        activeVisible: yield* adapter.isActiveVisible(anchor, target),
      } satisfies FollowEvent;
      yield* Ref.set(lastAnchorRef, anchor);
      yield* Ref.set(dirtyRef, false);
      return event;
    });

    const observedEvent = (type: 'user-scroll-end' | 'scroll-settled') =>
      Effect.gen(function* () {
        const anchor = yield* Ref.get(lastAnchorRef);
        const target = anchor ? yield* adapter.resolveTarget(anchor) : null;
        return {
          type,
          viewport: yield* adapter.getViewport,
          activeVisible: yield* adapter.isActiveVisible(anchor, target),
        } satisfies FollowEvent;
      });

    const interruptSettleWatch = Effect.gen(function* () {
      const fiber = yield* Ref.getAndSet(settleFiberRef, null);
      if (fiber) yield* Fiber.interrupt(fiber);
    });

    /** Engage samples a COHERENT anchor+viewport pair: wait for two
     * consecutive stable viewport readings first, so the target isn't
     * resolved against geometry that is still moving under it. */
    const waitForStableViewport = Effect.gen(function* () {
      let previous = yield* adapter.getViewport;
      let stablePolls = 0;
      for (let i = 0; i < ENGAGE_STABLE_MAX_POLLS; i++) {
        yield* Effect.sleep(Duration.millis(ENGAGE_STABLE_POLL_MS));
        const current = yield* adapter.getViewport;
        if (
          Math.abs(current.scrollY - previous.scrollY) <= SETTLE_TOLERANCE_PX
        ) {
          stablePolls++;
          if (stablePolls === 2) return;
        } else {
          stablePolls = 0;
        }
        previous = current;
      }
    });

    /** Idle gate at the head of `tick`: don't resolve a target against a
     * viewport that is mid-animation — the machine would only coalesce it
     * anyway. BOUNDED on purpose: `scrolling` is owned by follow-machine.ts,
     * and an unbounded wait here would turn any invariant break over there
     * into a permanently dead sampler with nothing logged. */
    const waitForMachineIdle = Effect.gen(function* () {
      for (let i = 0; i < MACHINE_IDLE_MAX_POLLS; i++) {
        if ((yield* Ref.get(stateRef)).scrolling !== 'animating') return;
        yield* Effect.sleep(Duration.millis(ENGAGE_STABLE_POLL_MS));
      }
      if ((yield* Ref.get(stateRef)).scrolling === 'animating')
        yield* Effect.logWarning(
          'read-along follow: machine stuck animating past the settle ceiling — sampling anyway',
        ).pipe(
          Effect.annotateLogs({
            waitedMs: MACHINE_IDLE_MAX_POLLS * ENGAGE_STABLE_POLL_MS,
          }),
        );
    });

    const swallowDefect =
      (site: string) =>
      <A, E, R>(effect: Effect.Effect<A, E, R>): Effect.Effect<void, E, R> =>
        effect.pipe(
          Effect.asVoid,
          Effect.catchDefect((defect) =>
            Effect.logWarning('read-along follow adapter defect').pipe(
              Effect.annotateLogs({ site, defect: String(defect) }),
            ),
          ),
        );

    const fallbackViewport = (target: ScrollTarget): FollowViewport => ({
      scrollY: target.kind === 'offset' ? target.y : 0,
      height: 0,
    });

    /** Settle detection for animated scrolls, shared across platforms: poll
     * the viewport until it arrives at the target (the resolvers clamp, so
     * the destination is always reachable). Runs as a supervised fiber;
     * cancel-scroll interrupts it. Always emits `scroll-settled`, including
     * on the give-up path — the machine must never be left animating. */
    const watchSettle = (targetY: number) =>
      Effect.gen(function* () {
        for (let i = 0; i < SETTLE_MAX_POLLS; i++) {
          yield* Effect.sleep(Duration.millis(SETTLE_POLL_MS));
          const viewport = yield* adapter.getViewport;
          if (Math.abs(viewport.scrollY - targetY) <= SETTLE_TOLERANCE_PX)
            break;
        }
      }).pipe(
        swallowDefect('watch-settle'),
        Effect.andThen(
          Effect.gen(function* () {
            yield* Ref.set(settleFiberRef, null);
            const settled = yield* observedEvent('scroll-settled').pipe(
              Effect.catchDefect(() =>
                Effect.succeed({
                  type: 'scroll-settled',
                  viewport: { scrollY: targetY, height: 0 },
                  activeVisible: false,
                } satisfies FollowEvent),
              ),
            );
            yield* Queue.offer(events, settled);
          }),
        ),
      );

    const execute = (command: FollowCommand): Effect.Effect<void> => {
      switch (command.type) {
        case 'scroll': {
          return Effect.gen(function* () {
            // A coarse page jump mounts the word's row — re-resolve on the
            // next tick even if the anchor hasn't changed, so the jump
            // refines to the word while paused too.
            if (command.target.kind === 'page') yield* Ref.set(dirtyRef, true);
            yield* Effect.logDebug('read-along follow scroll').pipe(
              Effect.annotateLogs({
                target: command.target,
                animated: command.animated,
              }),
            );
            const started = yield* adapter
              .executeScroll(command.target, { animated: command.animated })
              .pipe(
                Effect.as(true),
                Effect.catchDefect((defect) =>
                  Effect.logWarning('read-along follow scroll failed').pipe(
                    Effect.annotateLogs({ defect: String(defect) }),
                    Effect.as(false),
                  ),
                ),
              );
            if (
              started &&
              command.animated &&
              command.target.kind === 'offset'
            ) {
              const fiber = yield* watchSettle(command.target.y).pipe(
                Effect.forkIn(scope),
              );
              yield* Ref.set(settleFiberRef, fiber);
            } else if (started) {
              // Instant scrolls settle immediately.
              const settled = yield* observedEvent('scroll-settled').pipe(
                Effect.catchDefect(() =>
                  Effect.succeed({
                    type: 'scroll-settled',
                    viewport: fallbackViewport(command.target),
                    activeVisible: false,
                  } satisfies FollowEvent),
                ),
              );
              yield* Queue.offer(events, settled);
            } else {
              yield* Ref.set(dirtyRef, true);
              yield* Queue.offer(events, { type: 'layout-invalidated' });
            }
          });
        }
        case 'cancel-scroll': {
          return interruptSettleWatch.pipe(
            Effect.andThen(adapter.cancelScroll),
          );
        }
        case 'pill': {
          return adapter.onPillChange(command.visible);
        }
        default: {
          return command satisfies never;
        }
      }
    };

    const processEvent = (event: FollowEvent) =>
      Effect.gen(function* () {
        const state = yield* Ref.get(stateRef);
        const [next, commands] = followReduce(state, event, config);
        yield* Ref.set(stateRef, next);
        yield* Effect.forEach(
          commands,
          (command) => execute(command).pipe(swallowDefect('execute')),
          { discard: true },
        );
      });

    // Single consumer fiber: reduction is serialized, so commands can never
    // interleave and re-entrancy is impossible by construction.
    yield* Queue.take(events).pipe(
      Effect.flatMap((event) =>
        processEvent(event).pipe(swallowDefect('process-event')),
      ),
      Effect.forever,
      Effect.forkScoped,
    );

    const tick = Effect.gen(function* () {
      yield* waitForMachineIdle;
      const anchor = yield* adapter.getAnchor;
      const dirty = yield* Ref.get(dirtyRef);
      const last = yield* Ref.get(lastAnchorRef);
      if (!dirty && sameAnchor(anchor, last)) return;
      yield* Queue.offer(events, yield* anchorEvent);
    }).pipe(swallowDefect('sample-tick'));

    // Sampler fiber: first tick immediately, then on the spaced schedule.
    yield* tick.pipe(
      Effect.repeat(Schedule.spaced(Duration.millis(sampleIntervalMs))),
      Effect.forkScoped,
    );

    // Engage runs on its own fiber because it waits for the viewport to
    // stabilize first; a second engage (or any user gesture) must cancel the
    // pending one rather than race it to the queue.
    const engageFiberRef = yield* Ref.make<Fiber.Fiber<void> | null>(null);
    const interruptPendingEngage = Effect.gen(function* () {
      const fiber = yield* Ref.getAndSet(engageFiberRef, null);
      if (fiber) yield* Fiber.interrupt(fiber);
    });

    return {
      notifyUserScrollStart: Effect.gen(function* () {
        yield* interruptPendingEngage;
        yield* Queue.offer(events, { type: 'user-scroll-start' });
      }).pipe(Effect.asVoid),
      notifyUserScrollEnd: observedEvent('user-scroll-end').pipe(
        Effect.flatMap((event) => Queue.offer(events, event)),
        swallowDefect('user-scroll-end'),
      ),
      disengage: Effect.gen(function* () {
        yield* interruptPendingEngage;
        yield* Queue.offer(events, { type: 'user-scroll-start' });
        yield* Queue.offer(events, yield* observedEvent('user-scroll-end'));
      }).pipe(swallowDefect('disengage')),
      engage: Effect.gen(function* () {
        yield* interruptPendingEngage;
        const fiber = yield* Effect.gen(function* () {
          yield* waitForStableViewport;
          // ONE coherent sample feeds both the queue and the no-target check
          // below — re-resolving for the check could disagree with what the
          // machine was handed.
          const event = yield* anchorEvent;
          yield* Queue.offer(events, event);
          // Jump-to-current is explicit user intent: if the just-refreshed
          // anchor has no resolvable scroll target the press is a silent
          // no-op — surface that dead-end instead of failing quietly.
          if (event.target === null)
            yield* Effect.logWarning(
              'follow.engage: no resolvable scroll target — jump-to-current is a no-op',
            ).pipe(
              Effect.annotateLogs({
                hasAnchor: (yield* Ref.get(lastAnchorRef)) !== undefined,
              }),
            );
          yield* Queue.offer(events, { type: 'engage' });
        }).pipe(
          swallowDefect('engage'),
          Effect.ensuring(Ref.set(engageFiberRef, null)),
          Effect.forkIn(scope),
        );
        yield* Ref.set(engageFiberRef, fiber);
      }).pipe(Effect.asVoid),
      engageQuiet: Effect.gen(function* () {
        yield* interruptPendingEngage;
        yield* Queue.offer(events, { type: 'engage', immediateScroll: false });
        // Re-evaluate on the next tick even if the anchor doesn't change
        // (e.g. tapping the currently-spoken word).
        yield* Ref.set(dirtyRef, true);
        yield* tick;
      }),
      setPref: (enabled) =>
        Effect.gen(function* () {
          yield* Queue.offer(events, { type: 'pref', enabled });
          if (enabled) {
            yield* Ref.set(dirtyRef, true);
            yield* tick;
          }
        }),
      layoutInvalidated: Effect.gen(function* () {
        yield* Queue.offer(events, { type: 'layout-invalidated' });
        yield* Ref.set(dirtyRef, true);
        yield* tick;
      }),
      currentState: Ref.get(stateRef),
    } satisfies ReadAlongFollowHandle;
  });

export const readAlongFollowLayer = (
  options: ReadAlongFollowOptions,
): Layer.Layer<ReadAlongFollow, never, ReadAlongFollowAdapter> =>
  Layer.effect(ReadAlongFollow, makeReadAlongFollow(options));
